/**
 * LBR2-D2 careers-page — PUBLIC opening detail + apply (`/jobopenings/:id`).
 * Unauthenticated. 404s gracefully when the opening is closed / not found.
 * The JD description is rendered as plain text with preserved whitespace
 * (no dangerouslySetInnerHTML) — safe on a public page.
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Briefcase, Clock, Users, ArrowLeft, AlertCircle } from 'lucide-react';
import { CareersChrome } from './CareersChrome';
import { getOpening, type Opening } from './careersApi';
import { formatEmploymentType, formatExperienceRange, formatPostedDate } from './careersFormat';
import { ApplyForm } from './ApplyForm';

export function JobOpeningDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [opening, setOpening] = useState<Opening | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!id) return;
    setLoading(true);
    getOpening(id)
      .then((o) => {
        if (alive) {
          setOpening(o);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (alive) {
          setError(e.message);
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const exp = opening ? formatExperienceRange(opening.minExperienceYears, opening.maxExperienceYears) : null;

  return (
    <CareersChrome>
      <Link
        to="/jobopenings"
        className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" /> All positions
      </Link>

      {loading ? (
        <div className="py-16 text-center text-gray-400">Loading…</div>
      ) : error ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white py-16 text-center">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="font-semibold text-gray-700">{error}</p>
          <Link to="/jobopenings" className="mt-3 inline-block text-sm font-semibold text-[#f5870a]">
            Browse other positions
          </Link>
        </div>
      ) : opening ? (
        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h1 className="font-display text-2xl font-bold text-gray-900">{opening.jobTitle}</h1>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                <MapPin className="h-3 w-3" />
                {opening.location}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                <Briefcase className="h-3 w-3" />
                {formatEmploymentType(opening.employmentType)}
              </span>
              {exp && (
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                  <Clock className="h-3 w-3" />
                  {exp}
                </span>
              )}
              {opening.vacancies > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                  <Users className="h-3 w-3" />
                  {opening.vacancies} opening{opening.vacancies > 1 ? 's' : ''}
                </span>
              )}
            </div>
            {opening.postedDate && (
              <p className="mt-2 text-xs text-gray-400">Posted {formatPostedDate(opening.postedDate)}</p>
            )}
            {opening.requiredSkills?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {opening.requiredSkills.map((s) => (
                  <span
                    key={s}
                    className="rounded-md bg-orange-50 px-2 py-0.5 text-xs font-medium text-[#b5610a]"
                  >
                    {s}
                  </span>
                ))}
              </div>
            )}
            {opening.description && (
              <div className="mt-5 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                {opening.description}
              </div>
            )}
          </div>
          <ApplyForm jobOpeningId={opening.id} jobTitle={opening.jobTitle} />
        </div>
      ) : null}
    </CareersChrome>
  );
}

export default JobOpeningDetailPage;
